import { api } from './client'

/**
 * Generatore delle pagine progetto (flag progetti.generatore): i dati della proposta, la pagina scritta dall'IA, le versioni
 * e la pubblicazione su progetti.equalstudio.it. I prezzi vengono solo dai dati: l'IA scrive i testi.
 */
export interface ProjectPackageInput {
  nome: string
  /** Come si mostra ("3€", "€490"). */
  prezzo: string
  unita?: string | null
  descrizione?: string | null
  consigliato: boolean
}

export interface ProjectProposalInput {
  cliente: string
  slug?: string | null
  settore?: string | null
  sito?: string | null
  social?: string | null
  tipo?: string | null
  data?: string | null
  situazione?: string | null
  obiettivi?: string | null
  servizi?: string | null
  feeImporto: number
  feeImportoPieno?: number | null
  feeDescrizione?: string | null
  pacchetti: ProjectPackageInput[]
  tempi?: string | null
  note?: string | null
}

export type ProjectProposalStatus = 'bozza' | 'in_lavorazione' | 'pronta'
export type ProjectVersionKind = 'generazione' | 'modifica' | 'ripristino'

export interface ProjectProposalListItem {
  id: number
  slug: string
  clientName: string
  status: ProjectProposalStatus
  currentVersion: number
  publishedVersion: number | null
  publishedAt: string | null
  updatedAt: string
}

export interface ProjectProposalVersion {
  number: number
  kind: ProjectVersionKind
  instruction: string | null
  summary: string | null
  createdAt: string
}

export interface ProjectProposalDetail {
  id: number
  slug: string
  clientName: string
  status: ProjectProposalStatus
  input: ProjectProposalInput
  hasContent: boolean
  currentVersion: number
  pendingInstruction: string | null
  working: boolean
  error: string | null
  publishedVersion: number | null
  publishedAt: string | null
  publishConfigured: boolean
  publicUrl: string | null
  versions: ProjectProposalVersion[]
  updatedAt: string
}

const BASE = '/api/project-proposals'

export const projectProposalsApi = {
  list: () => api<ProjectProposalListItem[]>(BASE),
  get: (id: number) => api<ProjectProposalDetail>(`${BASE}/${id}`),
  create: (input: ProjectProposalInput) => api<ProjectProposalDetail>(BASE, { method: 'POST', body: JSON.stringify(input) }),
  updateInput: (id: number, input: ProjectProposalInput, regenerate: boolean) =>
    api<ProjectProposalDetail>(`${BASE}/${id}/input?regenerate=${regenerate}`, { method: 'PUT', body: JSON.stringify(input) }),
  revise: (id: number, instruction: string) =>
    api<ProjectProposalDetail>(`${BASE}/${id}/revise`, { method: 'POST', body: JSON.stringify({ instruction }) }),
  restore: (id: number, version: number) => api<ProjectProposalDetail>(`${BASE}/${id}/versions/${version}/restore`, { method: 'POST' }),
  page: (id: number, version?: number) =>
    api<{ html: string }>(`${BASE}/${id}/page${version ? `?version=${version}` : ''}`),
  publish: (id: number, password: string | null) =>
    api<{ url: string; detail: ProjectProposalDetail }>(`${BASE}/${id}/publish`, { method: 'POST', body: JSON.stringify({ password }) }),
  remove: (id: number) => api<void>(`${BASE}/${id}`, { method: 'DELETE' }),
}

export const PROPOSAL_STATUS: Record<ProjectProposalStatus, { label: string; tone: 'neutral' | 'info' | 'success' }> = {
  bozza: { label: 'Bozza', tone: 'neutral' },
  in_lavorazione: { label: 'L’IA sta scrivendo', tone: 'info' },
  pronta: { label: 'Pronta', tone: 'success' },
}

export const VERSION_KIND: Record<ProjectVersionKind, string> = {
  generazione: 'Generata dai dati',
  modifica: 'Modifica',
  ripristino: 'Ripristino',
}

export function emptyInput(): ProjectProposalInput {
  return {
    cliente: '', slug: '', settore: '', sito: '', social: '', tipo: 'Proposta commerciale', data: '',
    situazione: '', obiettivi: '', servizi: '', feeImporto: 0, feeImportoPieno: null, feeDescrizione: '',
    pacchetti: [{ nome: '', prezzo: '', unita: '', descrizione: '', consigliato: false }],
    tempi: '', note: '',
  }
}

/** Stesso calcolo del server (ProjectProposal.SlugFrom): l'indirizzo che avrà la pagina se non se ne indica uno. */
export function slugFrom(name: string): string {
  return name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60).replace(/-+$/, '')
}
